import { useCallback, useEffect, useState } from 'react';
import { Button, Radio } from '@ab-inbev-maz/compass';
import NavigationHeader from '@/components/navigation/NavigationHeader';
import Loading from '@/features/sso-auth/components/Loading';
import { DetailView } from '@/components/commos/DetailView';
import { useAlertStore } from '@/stores/alertStore';
import { hideGlobalModal, showGlobalModal } from '@/stores/modalStore';
import { countryService, type CountryTaxonomy } from '@/services/countryService';

const TAXONOMY_LABELS: Record<string, string> = {
    default: 'Vertical',
    default_top: 'Vertical - Espacio Superior (Peru)',
    lineal: 'Lineal',
    overlap: 'Sobrepuesto',
    grid: 'Cuadrícula',
};

const LAYOUT_OPTIONS = [
    {
        id: 'default',
        name: 'Vertical',
        description: 'Los SKU se apilarán de forma vertical, uno encima del otro, manteniendo la alineación central.',
        image: '/images/layout_vertical.png',
    },
    {
        id: 'default_top',
        name: 'Vertical - Espacio Superior',
        description: 'Los SKU se apilarán de forma vertical, uno encima del otro, manteniendo la alineación central.',
        image: '/images/layout_vertical_top.png',
    },
    {
        id: 'lineal',
        name: 'Lineal',
        description: 'Los SKU se alinearán dinámicamente uno junto a otro, manteniendo un espacio uniforme entre cada uno.',
        image: '/images/layout_linear.png',
    },
    {
        id: 'overlap',
        name: 'Sobrepuesto',
        description: 'Los SKU se superpondrán parcialmente entre sí, creando un efecto de profundidad y dinamismo.',
        image: '/images/layout_overlap.png',
    },
    {
        id: 'grid',
        name: 'Cuadrícula',
        description: 'Los SKU se distribuirán en una cuadrícula ordenada, ocupando el espacio de manera uniforme.',
        image: '/images/layout_grid.png',
    },
];

const SettingsPage = () => {
    const showAlert = useAlertStore(state => state.showAlert);

    const [country, setCountry] = useState<CountryTaxonomy | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [selectedTaxonomy, setSelectedTaxonomy] = useState<string>('');

    const fetchTaxonomy = useCallback(async () => {
        setIsLoading(true);
        const result = await countryService.getTaxonomy();
        if (result.success && result.data) {
            setCountry(result.data);
        } else {
            showAlert({
                text: 'Error al cargar configuración',
                message: result.error || 'No se pudo obtener la taxonomía del país',
                status: 'error',
            });
        }
        setIsLoading(false);
    }, [showAlert]);

    useEffect(() => {
        void fetchTaxonomy();
    }, [fetchTaxonomy]);

    const onSubmit = useCallback(() => {
        showGlobalModal({
            title: '¿Estás seguro de cambiar la taxonomía?',
            message: 'Estás a punto de cambiar la taxonomía del país. Esto afectará la composición visual de los combos generados.',
            iconType: 'warning',
            primaryAction: {
                label: 'Guardar',
                onClick: () => {
                    void (async () => {
                        setIsFetching(true);
                        hideGlobalModal();
                        const result = await countryService.updateTaxonomy(selectedTaxonomy);
                        if (result.success && result.data) {
                            setCountry(result.data);
                            setIsEditing(false);
                            showAlert({
                                text: 'Taxonomía actualizada',
                                message: `La taxonomía se cambió a "${TAXONOMY_LABELS[result.data.taxonomy] || result.data.taxonomy}"`,
                                status: 'success',
                            });
                        } else {
                            showAlert({
                                text: 'Error al actualizar taxonomía',
                                message: result.message || 'Ocurrió un error inesperado',
                                status: 'error',
                            });
                        }
                        setIsFetching(false);
                    })();
                },
                variant: 'primary',
            },
            secondaryAction: {
                label: 'Cancelar',
                onClick: () => {
                    hideGlobalModal();
                },
            },
        });
    }, [showAlert, selectedTaxonomy]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
                    <Loading message="Cargando configuración del país." />
                </div>
            </div>
        );
    }

    const detailData = country
        ? [
            { id: 'code', label: 'Código del país', value: country.code },
            { id: 'name', label: 'Nombre del país', value: country.name },
            { id: 'taxonomy', label: 'Taxonomía', value: TAXONOMY_LABELS[country.taxonomy] || country.taxonomy },
        ]
        : [];

    return (
        <div className="min-h-screen bg-white">
            <NavigationHeader
                title={`Configuración: ${country?.name || ''}`}
                description="Administra la taxonomía de composición visual para tu país"
                actions={[
                    isEditing ? (
                        <Button
                            key="cancel"
                            label="Cancelar"
                            variant="secondary"
                            onClick={() => setIsEditing(false)}
                            disabled={isFetching}
                        />
                    ) : null,
                    <Button
                        key="edit-save"
                        label={isEditing ? 'Guardar' : 'Editar'}
                        variant="primary"
                        rightIcon={isEditing ? 'functionalities/save' : 'functionalities/edit-2'}
                        onClick={isEditing ? onSubmit : () => {
                            setSelectedTaxonomy(country?.taxonomy || 'default');
                            setIsEditing(true);
                        }}
                        disabled={isFetching}
                    />,
                ].filter(Boolean)}
            />

            <hr className="border-gray-200 container mx-auto mt-2" />

            <div className="container mx-auto px-4 py-8">
                <p className="mb-3 text-sm font-medium text-gray-700">Taxonomía del país</p>
                <div>
                    {isEditing ? (
                        <div className={`grid gap-4 grid-cols-1 lg:grid-cols-2 ${isFetching ? 'opacity-50 pointer-events-none' : ''}`}>
                            {LAYOUT_OPTIONS.map((layout) => (
                                <label
                                    key={layout.id}
                                    className={`flex items-start gap-4 p-4 border rounded-lg hover:border-gray-400 transition-colors cursor-pointer ${selectedTaxonomy === layout.id ? 'border-gray-400 bg-gray-50' : ''
                                        }`}
                                >
                                    <Radio
                                        value={layout.id}
                                        name="taxonomy"
                                        checked={selectedTaxonomy === layout.id}
                                        onChange={() => setSelectedTaxonomy(layout.id)}
                                    />
                                    <img
                                        src={layout.image}
                                        alt={layout.name}
                                        className="w-[180px] h-[180px] object-contain rounded-sm border flex-shrink-0"
                                    />
                                    <div className="flex flex-col gap-1 flex-1">
                                        <span className="text-l font-semibold">{layout.name}</span>
                                        <span className="text-sm text-gray-500">{layout.description}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    ) : (
                        <DetailView data={detailData} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
